import { useCallback, useEffect, useState } from 'react';
import { socialApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import type { SocialCampaign, SocialCampaignRequest } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function SocialCampaignsPage() {
  const pagination = usePagination();
  const [campaigns, setCampaigns] = useState<SocialCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await socialApi.listCampaigns({ page: pagination.page, size: pagination.size });
      setCampaigns(res.content ?? []);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body: SocialCampaignRequest = { name, utmCampaign: utmCampaign || undefined };
      await socialApi.createCampaign(body);
      notify.success('Campaign created');
      setName('');
      setUtmCampaign('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const submitApproval = async (id: number) => {
    try {
      await socialApi.submitCampaignApproval(id);
      notify.success('Submitted for approval');
      load();
    } catch (err) {
      showApiError(err, 'Approval submit failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Social Campaigns"
        description="Cross-channel campaigns linked to Marketing — no duplicate campaign engine"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Social', to: '/social' }, { label: 'Campaigns' }]}
      />
      <div className="card mb-6 p-4">
        <h3 className="mb-3 text-sm font-semibold">New campaign</h3>
        <div className="flex flex-wrap gap-3">
          <Input className="min-w-[200px]" placeholder="Campaign name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input className="min-w-[160px]" placeholder="utm_campaign" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} />
          <Button onClick={create} disabled={saving}>{saving ? 'Saving…' : 'Create'}</Button>
        </div>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 dark:bg-slate-800/50">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">UTM</th>
              <th className="px-4 py-2 text-left">Scheduled</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-slate-500">Loading…</td></tr>
            ) : campaigns.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-slate-500">No campaigns yet</td></tr>
            ) : (
              campaigns.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-2">{c.name}</td>
                  <td className="px-4 py-2">{c.campaignStatus}</td>
                  <td className="px-4 py-2">{c.utmCampaign ?? '—'}</td>
                  <td className="px-4 py-2">{c.scheduledAt ? new Date(c.scheduledAt).toLocaleString() : '—'}</td>
                  <td className="px-4 py-2">
                    {c.campaignStatus !== 'APPROVED' && c.campaignStatus !== 'PUBLISHED' && (
                      <button type="button" className="text-primary-600 text-sm" onClick={() => submitApproval(c.id)}>
                        Submit approval
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={campaigns.length > 0}
        />
      </div>
    </div>
  );
}
